import React, { useEffect, useState } from "react";
import PrayerBoardBackDesk from "../components/PrayerBoardBackDesk";
import { getToday, getNotes } from "../api";
import IQAMAH from "../pages/main/pic/IQAMAH.mp3";
import TimeTable270 from "../pages/TimeTable270";
import Countdown3D from "../pages/Countdown3D";

import {
  todayMMDDYY,
  tomorrowMMDDYY,
  nowPretty,
  todayPretty,
  scheduleMidnightReload,
} from "../utils/date";

/* ---------------------------------------------------------
   ⭐ UNIVERSAL DATE NORMALIZER
--------------------------------------------------------- */
function normalizeDate(input) {
  if (!input) return null;
  input = input.trim();
  const parts = input.split("/");
  if (parts.length !== 3) return null;

  let [m, d, y] = parts;
  m = m.padStart(2, "0");
  d = d.padStart(2, "0");
  if (y.length === 2) y = "20" + y;

  return `${m}/${d}/${y}`;
}

/* ---------------------------------------------------------
   ⭐ FLEXIBLE DATE LOADER — TRIES MULTIPLE FORMATS
--------------------------------------------------------- */
async function getTodayFlexible() {
  const raw = todayMMDDYY();
  const norm = normalizeDate(raw);

  const candidates = new Set([
    raw,
    norm,
    norm ? `${norm.slice(0, 6)}${raw.slice(-2)}` : null,
    raw.replace(/^0/, ""),
    raw.replace(/\/0/, "/"),
    raw.replace(/^0/, "").replace(/\/0/, "/"),
  ]);

  for (const f of candidates) {
    if (!f) continue;
    try {
      const d = await getToday(f);
      if (d && Object.keys(d).length > 0) {
        console.log("✔ Loaded using date format:", f);
        return d;
      }
    } catch (e) {}
  }

  console.warn("⚠ No matching date format found for:", raw);
  return null;
}

export default function Rotate270() {
  const wrapperStyle = {
    width: "99vw",
    height: "98.1vh",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#eee",
  };

  const rotatedStyle = {
    width: "100vh", // swap width/height because of rotation
    height: "100vw",
    transform: "rotate(270deg)",
    transformOrigin: "center center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    // border: "1px solid #000",
  };

  const [data, setData] = useState(null);
  const [tData, setTData] = useState(null);
  const [now, setNow] = useState(nowPretty());
  const [today, setToday] = useState(todayPretty());
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const countdownRef = React.useRef(null);

  /* ---------------- NOTES ---------------- */
  useEffect(() => {
    getNotes().then(setNotes);
  }, []);

  /* ---------------- TODAY ---------------- */
  useEffect(() => {
    getTodayFlexible()
      .then((d) => setData(d))
      .finally(() => setIsLoading(false));

    const timer = setInterval(() => setNow(nowPretty()), 1000);
    scheduleMidnightReload();
    return () => clearInterval(timer);
  }, []);

  /* ---------------- TOMORROW ---------------- */
  useEffect(() => {
    const normTomorrow = normalizeDate(tomorrowMMDDYY());
    if (!normTomorrow) return;
    getToday(normTomorrow)
      .then(setTData)
      .catch(() => {});
  }, []);

  /* ---------------- REFRESH BEFORE IQAMAH ---------------- */

  useEffect(() => {
    if (!data) return;

    const now = new Date();
    const todaySeconds =
      now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

    Object.entries(durations).forEach(([key, minutes]) => {
      if (!data[key]) return;

      const targetSeconds = timeToSeconds(data[key]);
      if (targetSeconds == null) return;

      const delay = (targetSeconds - todaySeconds) * 1000;

      if (delay > 0) {
        /* ⭐ WHITE SCREEN COUNTDOWN — 60 seconds before iqamah ⭐ */
        const whiteDelay = delay - 60000; // 60 seconds before

        if (whiteDelay > 0) {
          setTimeout(() => {
            setWhiteCountdown(60); // start countdown

            const interval = setInterval(() => {
              setWhiteCountdown((prev) => {
                if (prev <= 1) {
                  clearInterval(interval);
                  return null; // end white screen
                }
                return prev - 1;
              });
            }, 1000);
          }, whiteDelay);
        }

        /* ⭐ NORMAL BLACKOUT (your existing code) ⭐ */
        setTimeout(() => {
          const weekday = new Date().toLocaleDateString(undefined, {
            weekday: "long",
          });

          if (key === "dhuhrIqamah" && weekday === "Friday") {
            triggerBlackout(60);
          } else {
            triggerBlackout(minutes);
          }
        }, delay);
      }
    });
  }, [data]);

  /* ---------------- BLACKOUT LOGIC ---------------- */
  function timeToSeconds(str) {
    if (!str) return null;
    const [h, m] = str.split(":").map(Number);
    return h * 3600 + m * 60;
  }

  const [whiteCountdown, setWhiteCountdown] = useState(null);
  const [blackout, setBlackout] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const durations = {
    fajrIqamah: 20,
    dhuhrIqamah: 20,
    asrIqamah: 20,
    maghribIqamah: 20,
    ishaIqamah: 20,
  };

  useEffect(() => {
    if (!data) return;

    const now = new Date();
    const todaySeconds =
      now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

    Object.entries(durations).forEach(([key, minutes]) => {
      if (!data[key]) return;

      const targetSeconds = timeToSeconds(data[key]);
      if (targetSeconds == null) return;

      const delay = (targetSeconds - todaySeconds) * 1000;

      if (delay > 0) {
        setTimeout(() => {
          const weekday = new Date().toLocaleDateString(undefined, {
            weekday: "long",
          });

          if (key === "dhuhrIqamah" && weekday === "Friday") {
            triggerBlackout(60);
          } else {
            triggerBlackout(minutes);
          }
        }, delay);
      }
    });
  }, [data]);

  function triggerBlackout(minutes) {
    setBlackout(true);
    setRemaining(minutes * 60);

    // Clear any previous interval
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    countdownRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          setBlackout(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function formatTime2(sec) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(
      2,
      "0",
    )}:${String(s).padStart(2, "0")}`;
  }

  /* ---------------- LOADING ---------------- */
  if (isLoading) return <div>Loading...</div>;

  /* ---------------- NO DATA ---------------- */
  if (!data) {
    return (
      <div style={{ textAlign: "center", padding: 40, fontSize: 40 }}>
        No prayer data available for today.
      </div>
    );
  }

  /* ---------------- MAIN UI ---------------- */
  return (
    <div style={wrapperStyle}>
      <div style={rotatedStyle}>
        <div>
          <TimeTable270 data={data} title="Staff View" />

          {/* ⭐ WHITE SCREEN COUNTDOWN ⭐ */}
          {whiteCountdown !== null && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 999998,
                background: "white",
                color: "black",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "50px",
                fontWeight: "bold",
              }}
            >
              {<Countdown3D />}
            </div>
          )}
          {/* ⭐ BLACKOUT OVERLAY ⭐ */}
          {blackout && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 999999,
                background: "black",
                color: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              {/* First 10 minutes */}
              {remaining > 600 && (
                <>
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />

                  <div
                    style={{
                      fontSize: 180,
                      fontWeight: "bold",
                      marginBottom: 40,
                      color: "#ffffff",
                      opacity: 0.2,
                    }}
                  >
                    SILENCE PLEASE 📵
                    {/* 🔇 */}
                  </div>
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <div style={{ fontSize: 50, marginBottom: 20, opacity: 0.2 }}>
                    Please, Turn off your Phone. <br />
                    Move Forward. Fill first ranks first. <br />
                    Heels, not toe in line. <br /> Shoulder to shoulder, not
                    arms. <br /> No talking. No whispering. <br /> Jajakallahu
                    Khairan.
                  </div>
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <div style={{ fontSize: 40, marginBottom: 20, opacity: 0.2 }}>
                    Will return in {formatTime2(remaining)}
                  </div>
                </>
              )}
              {remaining <= 600 && (
                <>
                  <marquee
                    direction="up"
                    behavior="scroll"
                    loop="infinite"
                    scrollAmount="1"
                    scrollDelay="90"
                    style={{
                      fontSize: 55,

                      padding: "12px",
                      color: "white",
                      marginBottom: 40,
                      height: 1360,
                      overflow: "hidden",
                    }}
                  >
                    1. اللّٰهُ أَكْبَر <br /> Allahu Akbar <br /> "Allah is the
                    greatest"
                    <br /> <br /> 2. أَسْتَغْفِرُ اللهَ، أَسْتَغْفِرُ اللهَ،
                    أَسْتَغْفِرُ اللهَ
                    <br />
                    Astaghfirullah, Astaghfirullah, Astaghfirullah
                    <br />
                    "I seek forgiveness from Allah"
                    <br /> <br />
                    3. اللَّهُمَّ أَعِنِّي عَلَى ذِكْرِكَ، وَشُكْرِكَ، وَحُسْنِ
                    عِبَادَتِكَ
                    <br /> Allahumma a'inni 'ala dhikrika, wa shukrika, wa husni
                    'ibadatika
                    <br /> <br />
                    4. ‏ اللَّهُمَّ أَنْتَ السَّلاَمُ وَمِنْكَ السَّلاَمُ
                    تَبَارَكْتَ يَا ذَا الْجَلاَلِ وَالإِكْرَامِ
                    <br /> Allahumma Antas-Salam wa minkas-salam. Tabarakta ya
                    Dhal-jalali wal- ikram. Allahumma Antas Salam Dua
                    <br />
                    <br /> 5. لَا إِلَهَ إِلَّا اللهُ وَحْدَهُ لَا شَرِيكَ لَهُ،
                    لَهُ الْمُلْكُ وَلَهُ الْحَمْدُ، وَهُوَ عَلَى كُلِّ شَيءٍ
                    قًدِيرٌ‏.‏ اَللَّهُمَّ لَا مَانِعَ لِمَا أَعْطَيتَ، وَلَا
                    مُعْطِيَ لِمَا مَنَعْتَ، وَلَا يَنْفَعُ ذَا الْجَدِّ مِنْكَ
                    الْجَدُّ
                    <br /> Lā ilāha illallāhu wahdahu lā syarika lahu,
                    lahul-mulku wa lahul-hamdu, wa Huwa 'alā kulli shay-in
                    Qadir. Allāhumma lā māni'a limā a'tayta, wa lā mu'tiya limā
                    mana'ta, wa lā yanfa'u zal-jaddi minkal-jaddu. <br />
                    <br />
                    6. Recite Ayat al-Kursi
                    <br />
                    <br />
                    7.Recite Sayyidul istighfar:
                    <br />
                    اللَّهُمَّ أَنْتَ رَبِّي لاَ إِلَهَ إِلاَّ أَنْتَ
                    خَلَقْتَنِي وَأَنَا عَبْدُكَ وَأَنَا عَلَى عَهْدِكَ
                    وَوَعْدِكَ مَا اسْتَطَعْتُ أَعُوذُ بِكَ مِنْ شَرِّ مَا
                    صَنَعْتُ أَبُوءُ لَكَ بِنِعْمَتِكَ عَلَىَّ وَأَبُوءُ لَكَ
                    بِذَنْبِي فَاغْفِرْ لِي فَإِنَّهُ لاَ يَغْفِرُ الذُّنُوبَ
                    إِلاَّ أَنْتَ
                    <br />
                    Allahumma anta Rabbi la ilaha illa anta, Khalaqtani wa ana
                    `Abduka, wa ana `ala `ahdika wa wa`dika mastata`tu, A`udhu
                    bika min Sharri ma sana`tu, abu'u Laka bini`matika `alaiya,
                    wa abu'u laka bidhanbi faghfir lee fa innahu la yaghfiru
                    adhdhunuba illa anta.
                    <br />
                    <br />
                  </marquee>

                  <div style={{ fontSize: 40, marginBottom: 20, opacity: 0.2 }}>
                    Will return in {formatTime2(remaining)}
                  </div>
                </>
              )}

              <div style={{ fontSize: 20, opacity: 0.2 }}>
                Application Developer: 845.514.8834 | Message to Suggest
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
